import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Lock } from "lucide-react";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { DETAIL_TABS, DETAIL_TAB_KEYS } from "../contract";
import useClient from "../hooks/useClient";
import useCrmMoney from "../hooks/useCrmMoney";
import useRole from "../hooks/useRole";
import { crmHref } from "../paths";
import { fmtCount } from "../format";
import CrmCard from "../ui/CrmCard";
import CrmTabs, { panelId, tabId } from "../ui/CrmTabs";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import EmptyState from "../ui/EmptyState";
import ClientHeader from "../detail/ClientHeader";
import ClientActions from "../detail/ClientActions";
import SummaryStrip from "../detail/SummaryStrip";
import ConsentCard from "../detail/ConsentCard";
import TimelineTab from "../detail/tabs/TimelineTab";
import SessionsTab from "../detail/tabs/SessionsTab";
import SessionNotesTab from "../detail/tabs/SessionNotesTab";
import PurchasesTab from "../detail/tabs/PurchasesTab";
import EmailsTab from "../detail/tabs/EmailsTab";
import NotesTab from "../detail/tabs/NotesTab";
// The pathways module's own component (contract v1.1 C1.06): the CRM only mounts it.
import ContactPathways from "../ContactPathways";
import { CRM } from "../../../constants/testIds/crm";

/**
 * An erased client (spec Privacy worked example): shown as "Erased client" with only the
 * anonymised purchase aggregates the practice keeps for accounting.
 */
function ErasedClient({ client }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  const money = useCrmMoney();
  return (
    <CrmCard tone="raised" data-testid={CRM.clientErased}>
      <h1 className="font-display text-4xl text-eh-ink">{t("privacy.erasedTitle")}</h1>
      <p className="mt-2 text-sm text-eh-muted">{t("privacy.erasedBody")}</p>
      <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
        <div><dt>{t("summary.spend")}</dt><dd className="text-lg font-semibold">{money(client.summary?.lifetime_spend_cents ?? client.lifetime_spend_cents)}</dd></div>
        <div><dt>{t("summary.sessions")}</dt><dd className="text-lg font-semibold">{fmtCount(client.summary?.sessions_total, lang)}</dd></div>
      </dl>
    </CrmCard>
  );
}

/**
 * Admin → CRM → Clients → one client (spec Screen 3): header with inline edits and the
 * stage control, the action row, the summary strip, the consent card, then the tabs
 * Timeline · Sessions · Session notes 🔒 (owner only) · Purchases · Emails · Notes ·
 * Pathways. The open tab is `?tab=` (other parameters kept); an unknown value, or a tab
 * the role may not open, falls back to Timeline without issuing its read.
 */
export default function CrmClientDetail() {
  const t = useT("crm");
  const { clientId } = useParams();
  const [params, setParams] = useSearchParams();
  const { can } = useRole();
  const { client, setClient, loading, loadError, reload, update, setStage, resumeAutomatic } = useClient(clientId);

  const available = DETAIL_TABS.filter((k) => k !== "session-notes" || can("viewSessionNotes"));
  const requested = params.get("tab");
  const tab = available.includes(requested) ? requested : "timeline";
  const choose = (key) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set("tab", key);
    if (key !== "pathways") next.delete("pathway");
    if (key !== "session-notes") next.delete("booking");
    return next;
  }, { replace: true });

  if (loadError) {
    return (
      <div data-testid={CRM.client} className="space-y-4">
        <Link to={crmHref("/clients")} className="inline-flex items-center gap-2 text-sm text-eh-muted hover:text-eh-ink">{t("detail.back")}</Link>
        <CrmLoadFailure error={loadError} onRetry={reload} testId={CRM.clientError} />
      </div>
    );
  }
  if (loading && !client) return <div data-testid={CRM.client} className="h-64 animate-pulse rounded-2xl bg-eh-cream" aria-busy="true" />;
  if (!client) return <EmptyState title={t("errors.clientNotFound")} />;
  if (client.erased_at) return <div data-testid={CRM.client}><ErasedClient client={client} /></div>;

  const tabs = available.map((key) => ({
    key,
    label: t(DETAIL_TAB_KEYS[key]),
    icon: key === "session-notes" ? <Lock size={14} aria-hidden="true" /> : null,
    testId: CRM.clientTab(key),
  }));

  const panel = {
    timeline: <TimelineTab clientId={client.id} />,
    sessions: <SessionsTab client={client} onChanged={reload} />,
    "session-notes": <SessionNotesTab client={client} />,
    purchases: <PurchasesTab clientId={client.id} />,
    emails: <EmailsTab clientId={client.id} />,
    notes: <NotesTab clientId={client.id} />,
    pathways: <ContactPathways key={client.primary_email} email={client.primary_email} focusPathwayId={params.get("pathway")} />,
  }[tab];

  return (
    <div data-testid={CRM.client} className="space-y-4">
      <Link to={crmHref("/clients")} className="inline-flex items-center gap-2 text-sm text-eh-muted hover:text-eh-ink">{t("detail.back")}</Link>
      <CrmCard className="space-y-5">
        <ClientHeader client={client} update={update} setStage={setStage} resumeAutomatic={resumeAutomatic} />
        <ClientActions client={client} setClient={setClient} onChanged={reload} />
        <div className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_18rem]">
          <SummaryStrip client={client} onChanged={reload} />
          <ConsentCard client={client} />
        </div>
        <CrmTabs tabs={tabs} value={tab} onChange={choose} label={t("detail.tabs")} />
        <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)}>{panel}</div>
      </CrmCard>
    </div>
  );
}
